import React, { Fragment, ReactNode, useMemo } from 'react';
import FormInputStyleProperties from '@ui/form/FormInputStyleProperties';
import Utilities from '@ui/utils/Utilities';
import _isEmpty from 'lodash/isEmpty';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useFieldContext } from '@ui/form/context';
import { FieldErrors } from '@ui/form/field/FieldErrors';

export type SelectFieldProps<TValue> = {
  placeholder?: string,
  label?: string,
  labelHint?: string,
  disabled?: boolean,
  className?: string,
  extraClassName?: string,
  hideErrorMessage?: boolean,
  options?: Option<TValue>[]
  portal?: boolean,
  groupOptions?: GroupOptions<TValue>[],
  onValueChange?: (val?: TValue) => any,
  optionsTextSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  size?: 'w-full' | 'small' | 'medium' | 'large' | 'xlarge',
  optionsSize?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge',
  noOptionsText?: string,
  emptyOption?: boolean
} & FormInputStyleProperties;

type Option<TValue> = {
  id: string,
  label: string | ReactNode,
  value: TValue,
  extraInfo?: string,
  iconArea?: ReactNode
}

type GroupOptions<TValue> = {
  label: string,
  options: Option<TValue>[]
}

const sizeToWidthClass: Record<string, string> = {
  'w-full': 'w-full',
  small: 'w-20',
  medium: 'w-32',
  large: 'w-56',
  xlarge: 'w-64',
};

const optionsSizeToWidthClass: Record<string, string> = {
  ...sizeToWidthClass,
  xxlarge: 'w-86',
};

export const SelectField = React.memo(function SelectField<TValue extends any>(
  {
    placeholder,
    label,
    isLoading,
    disabled = false,
    className,
    extraClassName,
    labelHint,
    hideErrorMessage = false,
    options,
    portal,
    groupOptions,
    optionsTextSize = 'sm',
    size = 'large',
    optionsSize,
    noOptionsText,
    emptyOption
}: SelectFieldProps<TValue>) {

  const field = useFieldContext<TValue | undefined>();

  const optionsMap = useMemo(() => {
    const map = new Map<TValue, Option<TValue | undefined>>();
    if (options) {
      options.forEach(option => map.set(option.value, option));
    }
    if (groupOptions) {
      groupOptions.forEach(group => {
        group.options.forEach(option => map.set(option.value, option));
      });
    }
    return map;
  }, [options, groupOptions]);

  const findOption = (val: any): Option<TValue | undefined> | undefined => {
    return val !== undefined ? optionsMap.get(val) : undefined;
  };

  const width = sizeToWidthClass[size] ?? 'w-56';
  const optionsWidth = optionsSize ? (optionsSizeToWidthClass[optionsSize] ?? width) : width;

  const hasError = field.state.meta.isTouched && !field.state.meta.isValid;

  const renderField = () => {
    return <>
      <div>
        <Listbox value={field.state.value} onChange={(val) => field.handleChange(val)} disabled={disabled}>
          {({ open }) => (
            <div>
              <div className="flex">
                {!!label && <div className="label">{label}</div>}
                {!!labelHint ? <span className="text-xs leading-6 ml-2 text-gray-500 italic">{labelHint}</span> : ''}
              </div>

              <div className="relative mt-0.5">
                <ListboxButton
                  className={Utilities.classNames(
                    !!className ? className : 'input relative cursor-default pl-4 bg-white',
                    Utilities.NVL(hasError, 'input-error'),
                    Utilities.NVL(isLoading, 'input-isLoading'),
                    Utilities.NVL(disabled, 'input-disabled'),
                    !hasError ? 'bg-white' : '',
                    'focus:outline-none',
                    'pr-4',
                    width,
                    extraClassName)
                  }>
                  <span className="inline-flex w-full truncate">
                    <span className={Utilities.classNames(
                      (!field.state.value || hasError) && placeholder ? 'text-slate-400' : '',
                      'truncate'
                    )}>
                      {field.state.value ? (findOption(field.state.value)?.label || placeholder) : placeholder}
                    </span>
                  </span>
                  <span
                    className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </ListboxButton>

                <ListboxOptions style={{ zIndex: 1000 }}
                                anchor="bottom start"
                                portal={portal}
                                className={`z-100 mt-1 max-h-60 overflow-auto rounded-md bg-white py-2 text-${optionsTextSize} shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${optionsWidth}`}>
                  {renderOptions(noOptionsText || 'No options defined')}
                </ListboxOptions>
              </div>
            </div>
          )}
        </Listbox>
      </div>
    </>;
  };

  const renderOption = (o: Option<TValue | undefined>) => {
    return <ListboxOption
      key={o.id}
      className={({ focus }) =>
        Utilities.classNames(
          focus ? 'bg-gray-100 text-white' : 'text-slate-900',
          'relative cursor-default select-none py-2 pl-3 pr-2',
        )
      }
      value={o.value}
    >
      {({ selected, focus }) => (
        <>
          <div className="flex">
            <span
              className={Utilities.classNames(selected ? 'font-bold' : 'font-normal', focus ? 'text-slate-800' : 'text-slate-500', 'truncate')}>
              {o.label}
            </span>
            {!!o.extraInfo && <div className="text-slate-500 ml-2 truncate">{o.extraInfo}</div>}
            {!!o.iconArea && <div className="text-slate-300 ml-2 flex items-center">{o.iconArea}</div>}
          </div>

          {selected ? (
            <span
              className={Utilities.classNames(
                focus ? 'text-white' : 'text-blue-500',
                'absolute inset-y-0 right-0 flex items-center pr-4',
              )}
            />
          ) : null}
        </>
      )}
    </ListboxOption>;
  };

  const renderOptions = (noOptionsText: string = 'No options defined') => {
    const hasOptions = !_isEmpty(options) || !_isEmpty(groupOptions);
    
    if (!hasOptions) {

      return (
        <div className="px-4 py-2 text-sm text-gray-500 cursor-default">
          {noOptionsText}
        </div>
      );
    }

    return (
      <>
        {emptyOption && renderOption({ id: 'EMPTY_OPTION', value: undefined, label: placeholder ?? 'None' })}
        {!_isEmpty(options) && options!.map(o => renderOption(o))}
        {!_isEmpty(groupOptions) && groupOptions!.map((go: GroupOptions<TValue>) => (
          <React.Fragment key={go.label}>
            <div className="block bg-gray-50 border-b border-gray-100 py-1.5 px-4 text-xs font-bold text-blue-800">
              &bull; {go.label}
            </div>
            {go.options!.map((o: Option<TValue>) => renderOption(o))}
          </React.Fragment>
        ))}
      </>
    );
  };

  return <>
    {renderField()}
    {!hideErrorMessage && <FieldErrors meta={field.state.meta} />}
  </>;

});